import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";

import { shortenAccountKey } from "@/helpers/shortenAccountKey";
import { shortenReceiverId } from "@/helpers/shortenReceiverId";

import "./styles.scss";

interface RecoveryQRModalProps {
  isVisible: boolean;
  transactionId: number;
  sep7Uri: string;
  recipientId: string;
  walletAddress: string;
  deadline?: string;
  onClose: () => void;
}

export const RecoveryQRModal: React.FC<RecoveryQRModalProps> = ({
  isVisible,
  sep7Uri,
  recipientId,
  walletAddress,
  deadline,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [copiedRecipient, setCopiedRecipient] = useState(false);
  const [copiedAccountId, setCopiedAccountId] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(sep7Uri).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyRecipient = () => {
    navigator.clipboard.writeText(recipientId).then(() => {
      setCopiedRecipient(true);
      setTimeout(() => setCopiedRecipient(false), 2000);
    });
  };

  const handleCopyAccountId = () => {
    navigator.clipboard.writeText(walletAddress).then(() => {
      setCopiedAccountId(true);
      setTimeout(() => setCopiedAccountId(false), 2000);
    });
  };

  if (!isVisible) return null;

  const formattedDeadline = deadline
    ? new Date(deadline).toLocaleString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : null;

  const shortWalletAddress = shortenAccountKey(walletAddress);
  const shortRecipientId = shortenReceiverId(recipientId);

  return (
    <div className="RecoveryQRModal__backdrop" onClick={onClose}>
      <div
        className="RecoveryQRModal__card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close icon */}
        <button className="RecoveryQRModal__close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <div className="RecoveryQRModal__header">
          <h2 className="RecoveryQRModal__title">Approve account recovery</h2>
          <div className="RecoveryQRModal__meta">
            <div className="RecoveryQRModal__meta-row">
              <span className="RecoveryQRModal__meta-label">Requested by</span>
              <span className="RecoveryQRModal__meta-value">{shortRecipientId}</span>
              <button className="RecoveryQRModal__meta-copy" onClick={handleCopyRecipient} title="Copy email">
                {copiedRecipient
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                }
              </button>
            </div>
            <div className="RecoveryQRModal__meta-row">
              <span className="RecoveryQRModal__meta-label">Account ID</span>
              <span className="RecoveryQRModal__meta-value">{shortWalletAddress}</span>
              <button className="RecoveryQRModal__meta-copy" onClick={handleCopyAccountId} title="Copy full account ID">
                {copiedAccountId
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                }
              </button>
            </div>
          </div>
        </div>

        {/* Main content: two columns */}
        <div className="RecoveryQRModal__content">
          {/* Left column: steps */}
          <div className="RecoveryQRModal__steps">
            <div className="RecoveryQRModal__step">
              <span className="RecoveryQRModal__step-badge">1</span>
              <span className="RecoveryQRModal__step-text">
                Open VIA Wallet and scan this QR code.
              </span>
            </div>
            <div className="RecoveryQRModal__step">
              <span className="RecoveryQRModal__step-badge">2</span>
              <span className="RecoveryQRModal__step-text">
                Approve the request in VIA Wallet using your{" "}
                <strong>organization's account key.</strong>
              </span>
            </div>
            <div className="RecoveryQRModal__step">
              <span className="RecoveryQRModal__step-badge">3</span>
              <span className="RecoveryQRModal__step-text">
                Share{" "}
                <span className="RecoveryQRModal__recipient-pill">{shortRecipientId}</span>
                {"'s"} details with VIA, including their email address or phone number and account ID.
              </span>
            </div>
          </div>

          {/* Right column: QR code + copy button */}
          <div className="RecoveryQRModal__qr-column">
            <div className="RecoveryQRModal__qr-wrapper">
              <QRCodeSVG value={sep7Uri} size={200} />
            </div>
            <button className="RecoveryQRModal__copy-btn" onClick={handleCopy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              {copied ? "Copied!" : "Copy QR code"}
            </button>
          </div>
        </div>

        {/* Deadline banner */}
        {formattedDeadline && (
          <div className="RecoveryQRModal__deadline">
            <svg className="RecoveryQRModal__deadline-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <div className="RecoveryQRModal__deadline-text">
              <span>
                To make sure{" "}
                <span className="RecoveryQRModal__deadline-emphasis">{shortRecipientId}</span>
                {" "}can recover their funds, complete these steps by{" "}
                <span className="RecoveryQRModal__deadline-emphasis">{formattedDeadline}</span>.
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="RecoveryQRModal__footer">
          <button className="RecoveryQRModal__close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
